import { useEffect, useMemo, useRef, useState } from "react";
import type { ContentBundle, Locale } from "../api/types";
import { LanguagePicker } from "../components/LanguagePicker";
import { FallbackBanner } from "../components/FallbackBanner";
import { localUi } from "../i18n/uiFallback";

type Message =
  | { kind: "system"; text: string }
  | { kind: "divider"; text: string; glyph?: string }
  | {
      kind: "bot";
      text: string;
      emphasis?: boolean;
      argId?: string;
      stat?: { value: string; unit: string | null };
      sourceUrl?: string | null;
      evidencePublisher?: string | null;
      evidenceTitle?: string | null;
    }
  | { kind: "chart"; dataset: "climate" | "land" | "water" }
  | { kind: "story"; portrait: string; quote: string; name: string; role: string }
  | { kind: "faqs"; faqs: ContentBundle["faqs"] }
  | { kind: "step"; num: number; h: string; b: string }
  | {
      kind: "choice";
      options: { label: string; advance?: true; end?: true; switch?: true; positive?: true }[];
    };

interface Props {
  bundle: ContentBundle;
  tone: "soft" | "direct";
  locales: Locale[];
  requestedLocale: string;
  onLocale: (code: string) => void;
  savedIds: string[];
  toggleSaved: (id: string) => void;
  onBack: () => void;
}

function buildScript(bundle: ContentBundle): Message[] {
  const script: Message[] = [];
  const t = (k: string) => bundle.ui[k] ?? k;

  script.push({
    kind: "system",
    text: `${t("brand").toUpperCase()} · ${bundle.audience_label.toUpperCase()}`,
  });
  if (bundle.letter_paragraphs[0]) script.push({ kind: "bot", text: bundle.letter_paragraphs[0] });
  if (bundle.letter_paragraphs[1]) script.push({ kind: "bot", text: bundle.letter_paragraphs[1] });

  script.push({
    kind: "choice",
    options: [
      { label: localUi(bundle.locale, "chat_listening"), advance: true },
      { label: localUi(bundle.locale, "chat_what"), advance: true },
    ],
  });

  if (bundle.letter_paragraphs[2]) script.push({ kind: "bot", text: bundle.letter_paragraphs[2] });

  bundle.flow.forEach((cat, ci) => {
    const args = (bundle.arguments_by_category[cat.slug] ?? []).slice(0, 2);
    script.push({
      kind: "divider",
      text: `${t("chapter")} ${String(ci + 1).padStart(2, "0")} · ${cat.label}`,
      glyph: cat.glyph,
    });
    args.forEach((a) => {
      script.push({ kind: "bot", text: a.header, emphasis: true });
      script.push({
        kind: "bot",
        text: a.body,
        argId: a.slug,
        stat: a.stat_value ? { value: a.stat_value, unit: a.stat_unit } : undefined,
        sourceUrl: a.source_url,
        evidencePublisher: a.evidence_publisher,
        evidenceTitle: a.evidence_title,
      });
    });
    if (ci < bundle.flow.length - 1) {
      script.push({
        kind: "choice",
        options: [
          { label: localUi(bundle.locale, "chat_go_on"), advance: true },
          { label: localUi(bundle.locale, "chat_proof"), advance: true },
        ],
      });
    }
  });

  const chartCats = new Set(["climate", "hunger", "science"]);
  const chartCat = bundle.flow.find((c) => chartCats.has(c.slug));
  if (chartCat) {
    const dataset = chartCat.slug === "hunger" ? "land" : "climate";
    script.push({ kind: "chart", dataset });
  }

  if (bundle.story) {
    script.push({ kind: "system", text: t("story_title").toUpperCase() });
    script.push({
      kind: "story",
      portrait: bundle.story.portrait,
      quote: bundle.story.quote,
      name: bundle.story.name,
      role: bundle.story.role,
    });
  }

  if (bundle.faqs.length) {
    script.push({ kind: "system", text: t("faq_title").toUpperCase() });
    script.push({ kind: "faqs", faqs: bundle.faqs });
  }

  script.push({ kind: "system", text: t("start_title").toUpperCase() });
  bundle.steps.forEach((s, i) =>
    script.push({ kind: "step", num: i + 1, h: s.header, b: s.body }),
  );

  script.push({
    kind: "bot",
    text: `${t("letter_close")} ${t("letter_sign")}`,
    emphasis: true,
  });
  script.push({
    kind: "choice",
    options: [
      { label: localUi(bundle.locale, "chat_think"), end: true },
      { label: localUi(bundle.locale, "chat_try"), end: true, positive: true },
      { label: localUi(bundle.locale, "chat_switch"), switch: true },
    ],
  });

  return script;
}

export function ChatScreen({
  bundle,
  locales,
  requestedLocale,
  onLocale,
  savedIds,
  toggleSaved,
  onBack,
}: Props) {
  const t = (k: string) => bundle.ui[k] ?? k;
  const script = useMemo(() => buildScript(bundle), [bundle]);
  const [cursor, setCursor] = useState(0);
  const [typing, setTyping] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const timerRef = useRef<number | null>(null);
  const reducedMotion = useMemo(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches,
    [],
  );

  useEffect(() => {
    setCursor(0);
  }, [bundle.audience_slug, bundle.locale, bundle.tone]);

  useEffect(() => {
    if (cursor >= script.length) return undefined;
    const m = script[cursor];
    if (m.kind === "choice") return undefined;
    const isBot = m.kind === "bot";
    const delay = reducedMotion
      ? 0
      : isBot
        ? Math.min(620, 180 + ((m as { text: string }).text.length || 30) * 6)
        : 200;
    if (isBot && !reducedMotion) setTyping(true);
    timerRef.current = window.setTimeout(() => {
      setTyping(false);
      setCursor((c) => c + 1);
    }, delay);
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, [cursor, script, reducedMotion]);

  useEffect(() => {
    const el = wrapRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [cursor, typing]);

  const fastForward = () => {
    if (cursor >= script.length) return;
    const m = script[cursor];
    if (m.kind === "choice") return;
    if (timerRef.current) window.clearTimeout(timerRef.current);
    setTyping(false);
    setCursor((c) => c + 1);
  };

  const visible = script.slice(0, cursor);
  const current = script[cursor];
  const isChoice = current?.kind === "choice";
  const progress = Math.min(1, cursor / Math.max(1, script.length));

  return (
    <div className="conv">
      <div className="conv__topbar">
        <button className="chip-btn" onClick={onBack}>
          ← {t("back")}
        </button>
        <div className="conv__topbar-mid">
          <LanguagePicker
            locales={locales}
            value={requestedLocale}
            onChange={onLocale}
            variant="chip"
            label={t("lang")}
          />
        </div>
        <div className="conv__progress">
          <div className="conv__progress-bar" style={{ width: `${progress * 100}%` }} />
        </div>
      </div>

      <FallbackBanner
        bundle={bundle}
        requestedLocale={requestedLocale}
        requestedLabel={locales.find((l) => l.code === requestedLocale)?.display_name}
      />

      <div
        className="conv__stream"
        ref={wrapRef}
        onClick={fastForward}
        role="log"
        aria-live="polite"
      >
        <div className="conv__stream-inner">
          {visible.map((m, i) => (
            <MessageView
              key={i}
              m={m}
              t={t}
              savedIds={savedIds}
              toggleSaved={toggleSaved}
              bundle={bundle}
            />
          ))}
          {typing && <TypingDots />}
        </div>
      </div>

      <div className="conv__dock">
        {isChoice && current.kind === "choice" ? (
          <div className="conv__choices">
            {current.options.map((opt, i) => (
              <button
                key={i}
                className={`reply ${opt.positive ? "reply--positive" : ""}`}
                onClick={() => {
                  if (opt.switch || opt.end) {
                    onBack();
                    return;
                  }
                  setCursor((c) => c + 1);
                }}
              >
                <span className="reply__label">{opt.label}</span>
                <span className="reply__arrow">→</span>
              </button>
            ))}
          </div>
        ) : (
          <div className="conv__hint">
            <span className="conv__hint-dot" />
            {localUi(bundle.locale, "chat_typing")}
          </div>
        )}
      </div>
    </div>
  );
}

function MessageView({
  m,
  t,
  savedIds,
  toggleSaved,
  bundle,
}: {
  m: Message;
  t: (k: string) => string;
  savedIds: string[];
  toggleSaved: (id: string) => void;
  bundle: ContentBundle;
}) {
  switch (m.kind) {
    case "system":
      return <div className="m-system">— {m.text} —</div>;
    case "divider":
      return (
        <div className="m-divider">
          <span className="m-divider__glyph">{m.glyph}</span>
          <span className="m-divider__text">{m.text}</span>
          <span className="m-divider__line" />
        </div>
      );
    case "bot":
      return (
        <div className={`m-bot ${m.emphasis ? "m-bot--emph" : ""}`}>
          <div className="m-bot__avatar">▼</div>
          <div className="m-bot__body">
            <div className="m-bot__bubble">
              <p>{m.text}</p>
              {m.stat && (
                <div className="m-stat">
                  <span className="m-stat__v">{m.stat.value}</span>
                  {m.stat.unit && <span className="m-stat__u">{m.stat.unit}</span>}
                </div>
              )}
              {m.argId && (
                <div className="m-bot__tools">
                  <button
                    className={`pill-btn ${savedIds.includes(m.argId) ? "pill-btn--on" : ""}`}
                    onClick={() => toggleSaved(m.argId!)}
                  >
                    {savedIds.includes(m.argId) ? "★" : "☆"} {t("save")}
                  </button>
                  <button
                    className="pill-btn"
                    onClick={() => navigator.clipboard?.writeText(m.text)}
                  >
                    → {t("share")}
                  </button>
                </div>
              )}
              {m.evidencePublisher && (
                <div className="m-bot__cite">
                  {m.sourceUrl ? (
                    <a href={m.sourceUrl} target="_blank" rel="noreferrer noopener">
                      {m.evidencePublisher}
                      {m.evidenceTitle ? ` — ${m.evidenceTitle}` : ""}
                    </a>
                  ) : (
                    <span>
                      {m.evidencePublisher}
                      {m.evidenceTitle ? ` — ${m.evidenceTitle}` : ""}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      );
    case "chart": {
      const data = bundle.charts.find((c) => c.slug === m.dataset);
      if (!data) return null;
      const max = Math.max(...data.bars.map((b) => b.value));
      return (
        <div className="m-chart">
          <div className="m-chart__head">
            <span className="m-chart__label">{t("stats_title")}</span>
            <h4>{data.title}</h4>
          </div>
          <div className="m-chart__bars">
            {data.bars.map((b, i) => {
              const w = (b.value / max) * 100;
              return (
                <div key={b.label} className={`m-chart__bar ${b.is_plant ? "is-plant" : ""}`}>
                  <span className="m-chart__l">{b.label}</span>
                  <span className="m-chart__t">
                    <span
                      className="m-chart__f"
                      style={{ width: `${w}%`, transitionDelay: `${i * 70}ms` }}
                    />
                  </span>
                  <span className="m-chart__v">{b.value.toLocaleString(bundle.locale)}</span>
                </div>
              );
            })}
          </div>
          {data.source && <div className="m-chart__src">{data.source}</div>}
        </div>
      );
    }
    case "story":
      return (
        <div className="m-story">
          <div className="m-story__avatar">{m.portrait}</div>
          <div className="m-story__body">
            <p>“{m.quote}”</p>
            <div className="m-story__by">
              <strong>{m.name}</strong>
              <span>{m.role}</span>
            </div>
          </div>
        </div>
      );
    case "faqs":
      return <FaqMsg faqs={m.faqs} t={t} />;
    case "step":
      return (
        <div className="m-step">
          <div className="m-step__num">
            {t("step")} {String(m.num).padStart(2, "0")}
          </div>
          <div className="m-step__body">
            <h4>{m.h}</h4>
            <p>{m.b}</p>
          </div>
          <div className="m-step__bar" />
        </div>
      );
    case "choice":
      return null;
  }
}

function TypingDots() {
  return (
    <div className="m-bot m-bot--typing">
      <div className="m-bot__avatar">▼</div>
      <div className="m-bot__body">
        <div className="m-bot__bubble m-bot__bubble--dots">
          <span />
          <span />
          <span />
        </div>
      </div>
    </div>
  );
}

function FaqMsg({ faqs, t }: { faqs: ContentBundle["faqs"]; t: (k: string) => string }) {
  const [open, setOpen] = useState<string | null>(null);
  return (
    <div className="m-faq">
      {faqs.map((f) => {
        const isOpen = open === f.slug;
        return (
          <div key={f.slug} className={`m-faq__item ${isOpen ? "is-open" : ""}`}>
            <button className="m-faq__q" onClick={() => setOpen(isOpen ? null : f.slug)}>
              <span className="m-faq__obj">{t("objection")}</span>
              <span className="m-faq__qt">{f.question}</span>
              <span className="m-faq__tog">{isOpen ? "−" : "+"}</span>
            </button>
            {isOpen && (
              <div className="m-faq__a">
                <span className="m-faq__counter">{t("counter")}</span>
                <p>{f.answer}</p>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
